import {
  parseAdminPublicEvent,
  parseAdminPublicEventsResponse,
  parseAdminPublicSettings,
  parseCompletePublicEventAttachmentUploadRequest,
  parseCreateAdminPublicEventAttachmentRequest,
  parseCreateAdminPublicEventRequest,
  parsePublicEventDetail,
  parsePublicEventAttachmentUploadSessionResponse,
  parsePublicEventsResponse,
  parsePublicScheduleResponse,
  parsePublicTenant,
  parseUpdateAdminPublicEventAttachmentRequest,
  parseUpdateAdminPublicEventRequest,
  parseUpdateAdminPublicSettingsRequest
} from "@madamuh/domain";
import type {
  AdminPublicEventDto,
  AdminPublicEventsResponseDto,
  AdminPublicSettingsDto,
  CompletePublicEventAttachmentUploadRequestDto,
  CreateAdminPublicEventAttachmentRequestDto,
  CreateAdminPublicEventRequestDto,
  PublicEventDetailDto,
  PublicEventAttachmentUploadSessionResponseDto,
  PublicEventsResponseDto,
  PublicScheduleResponseDto,
  PublicTenantDto,
  UpdateAdminPublicEventAttachmentRequestDto,
  UpdateAdminPublicEventRequestDto,
  UpdateAdminPublicSettingsRequestDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

export async function fetchPublicTenant(tenantSlug: string): Promise<PublicTenantDto> {
  const response = await apiGet<unknown>(`/api/v1/public/tenants/${tenantSlug}`);
  return parsePublicTenant(response);
}

export async function fetchPublicSchedule(tenantSlug: string): Promise<PublicScheduleResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/public/tenants/${tenantSlug}/schedule`);
  return parsePublicScheduleResponse(response);
}

export async function fetchPublicEvents(tenantSlug: string): Promise<PublicEventsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/public/tenants/${tenantSlug}/events`);
  return parsePublicEventsResponse(response);
}

export async function fetchPublicEvent(tenantSlug: string, eventId: string): Promise<PublicEventDetailDto> {
  const response = await apiGet<unknown>(`/api/v1/public/tenants/${tenantSlug}/events/${eventId}`);
  return parsePublicEventDetail(response);
}

export async function fetchAdminPublicSettings(tenantSlug: string): Promise<AdminPublicSettingsDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/public-settings`);
  return parseAdminPublicSettings(response);
}

export async function patchAdminPublicSettings(
  tenantSlug: string,
  request: UpdateAdminPublicSettingsRequestDto
): Promise<AdminPublicSettingsDto> {
  const parsedRequest = parseUpdateAdminPublicSettingsRequest(request);
  const response = await apiPatch<UpdateAdminPublicSettingsRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-settings`,
    parsedRequest
  );

  return parseAdminPublicSettings(response);
}

export async function fetchAdminPublicEvents(tenantSlug: string): Promise<AdminPublicEventsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/public-events`);
  return parseAdminPublicEventsResponse(response);
}

export async function createAdminPublicEvent(
  tenantSlug: string,
  request: CreateAdminPublicEventRequestDto
): Promise<AdminPublicEventDto> {
  const parsedRequest = parseCreateAdminPublicEventRequest(request);
  const response = await apiPost<CreateAdminPublicEventRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events`,
    parsedRequest
  );

  return parseAdminPublicEvent(response);
}

export async function fetchAdminPublicEvent(
  tenantSlug: string,
  eventId: string
): Promise<AdminPublicEventDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}`);
  return parseAdminPublicEvent(response);
}

export async function patchAdminPublicEvent(
  tenantSlug: string,
  eventId: string,
  request: UpdateAdminPublicEventRequestDto
): Promise<AdminPublicEventDto> {
  const parsedRequest = parseUpdateAdminPublicEventRequest(request);
  const response = await apiPatch<UpdateAdminPublicEventRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}`,
    parsedRequest
  );

  return parseAdminPublicEvent(response);
}

export async function createAdminPublicEventAttachment(
  tenantSlug: string,
  eventId: string,
  request: CreateAdminPublicEventAttachmentRequestDto
): Promise<AdminPublicEventDto> {
  const parsedRequest = parseCreateAdminPublicEventAttachmentRequest(request);
  const response = await apiPost<CreateAdminPublicEventAttachmentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments`,
    parsedRequest
  );

  return parseAdminPublicEvent(response);
}

export async function createAdminPublicEventAttachmentUploadSession(
  tenantSlug: string,
  eventId: string,
  attachmentId: string
): Promise<PublicEventAttachmentUploadSessionResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments/${attachmentId}/upload-session`,
    {}
  );

  return parsePublicEventAttachmentUploadSessionResponse(response);
}

export async function completeAdminPublicEventAttachmentUpload(
  tenantSlug: string,
  eventId: string,
  attachmentId: string,
  request: CompletePublicEventAttachmentUploadRequestDto
): Promise<AdminPublicEventDto> {
  const parsedRequest = parseCompletePublicEventAttachmentUploadRequest(request);
  const response = await apiPost<CompletePublicEventAttachmentUploadRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments/${attachmentId}/complete`,
    parsedRequest
  );

  return parseAdminPublicEvent(response);
}

export async function patchAdminPublicEventAttachment(
  tenantSlug: string,
  eventId: string,
  attachmentId: string,
  request: UpdateAdminPublicEventAttachmentRequestDto
): Promise<AdminPublicEventDto> {
  const parsedRequest = parseUpdateAdminPublicEventAttachmentRequest(request);
  const response = await apiPatch<UpdateAdminPublicEventAttachmentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments/${attachmentId}`,
    parsedRequest
  );

  return parseAdminPublicEvent(response);
}

export async function hideAdminPublicEventAttachment(
  tenantSlug: string,
  eventId: string,
  attachmentId: string
): Promise<AdminPublicEventDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments/${attachmentId}/hide`,
    {}
  );

  return parseAdminPublicEvent(response);
}

export async function republishAdminPublicEventAttachment(
  tenantSlug: string,
  eventId: string,
  attachmentId: string
): Promise<AdminPublicEventDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/public-events/${eventId}/attachments/${attachmentId}/republish`,
    {}
  );

  return parseAdminPublicEvent(response);
}
